import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiFetch, uploadImage, resolveImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";
import { inputCls, labelCls } from "@/components/AuthShell";
import { ArrowLeft, Camera } from "lucide-react";

export default function EditProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: user.first_name || "", last_name: user.last_name || "", phone: user.phone || "",
    location: user.location || "", email: user.email || "", avatar_url: user.avatar_url || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const changeAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const url = await uploadImage(file); setForm({ ...form, avatar_url: url }); }
    catch (err) { toast.error(err.message); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiFetch("/me", { method: "PUT", body: JSON.stringify(form) });
      setUser(res.user);
      if (res.email_changed) { toast.success("Email updated — verify your new address"); return navigate("/verify-email"); }
      if (res.verification_reset) toast("Your details changed, so your Verified badge was removed.");
      else toast.success("Profile updated");
      navigate("/profile");
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-8 sm:px-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-full p-1.5 hover:bg-secondary"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-xl font-extrabold">Edit profile</h1>
      </div>

      <div className="mt-6 flex justify-center">
        <label className="relative cursor-pointer" data-testid="avatar-upload">
          <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-brand text-2xl font-bold text-white">
            {form.avatar_url ? <img src={resolveImage(form.avatar_url)} alt="" className="h-full w-full object-cover" /> : initials(`${form.first_name} ${form.last_name}`)}
          </div>
          <span className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-price text-white"><Camera className="h-4 w-4" /></span>
          <input type="file" accept="image/*" hidden onChange={changeAvatar} />
        </label>
      </div>

      <div className="mt-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>First name</label><input data-testid="edit-first-name" value={form.first_name} onChange={set("first_name")} className={inputCls} /></div>
          <div><label className={labelCls}>Last name</label><input data-testid="edit-last-name" value={form.last_name} onChange={set("last_name")} className={inputCls} /></div>
        </div>
        <div><label className={labelCls}>Phone</label><input data-testid="edit-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></div>
        <div><label className={labelCls}>Location</label><input data-testid="edit-location" value={form.location} onChange={set("location")} className={inputCls} /></div>
        <div><label className={labelCls}>Email</label><input data-testid="edit-email" type="email" value={form.email} onChange={set("email")} className={inputCls} /><p className="mt-1 text-xs text-muted-foreground">Changing email requires re-verification.</p></div>
        <button onClick={save} disabled={saving} className="w-full rounded-xl bg-price py-3 text-sm font-semibold text-white disabled:opacity-60" data-testid="save-profile">{saving ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
}
